import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ArmyBuilderConfig } from '../global/config';
import { HttpClientWithInFlightCache } from '../global/httpClient';
import { AppEvent, EventRegistration, EventRound, EventStanding, ForceEventSubmission } from './models';

export interface EventListFilter {
    title?: string;
    startDateFrom?: string;
    startDateTo?: string;
    countryCode?: string;
    visibility?: string;
}

@Injectable({ providedIn: 'root' })
export class EventService {
    private config = inject(ArmyBuilderConfig);
    private http = inject(HttpClientWithInFlightCache);

    private get baseUrl() {
        return `${this.config.apiBaseUrl}/events`;
    }

    listEvents(gameId: string, filters?: EventListFilter): Observable<AppEvent[]> {
        const params = new URLSearchParams({ gameId });
        if (filters?.title) params.set('title', filters.title);
        if (filters?.startDateFrom) params.set('startDateFrom', filters.startDateFrom);
        if (filters?.startDateTo) params.set('startDateTo', filters.startDateTo);
        if (filters?.countryCode) params.set('countryCode', filters.countryCode);
        if (filters?.visibility) params.set('visibility', filters.visibility);
        return this.http.get(`${this.baseUrl}?${params.toString()}`, { requiresLogin: false });
    }

    listMyEvents(gameId: string): Observable<AppEvent[]> {
        const params = new URLSearchParams({ gameId });
        return this.http.get(`${this.baseUrl}/mine?${params.toString()}`, { requiresLogin: true });
    }

    listPendingApprovals(gameId?: string): Observable<AppEvent[]> {
        const params = new URLSearchParams();
        if (gameId) params.set('gameId', gameId);
        const query = params.toString();
        return this.http.get(`${this.baseUrl}/pending-approvals${query ? '?' + query : ''}`, { requiresLogin: true });
    }

    approveEvent(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/approve`, {}, { requiresLogin: true });
    }

    rejectEvent(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/reject`, {}, { requiresLogin: true });
    }

    getEvent(id: string): Observable<AppEvent> {
        return this.http.get(`${this.baseUrl}/${id}`, { requiresLogin: false });
    }

    createEvent(event: Partial<AppEvent>): Observable<AppEvent> {
        return this.http.post(`${this.baseUrl}`, event, { requiresLogin: true });
    }

    updateEvent(id: string, event: Partial<AppEvent>): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}`, event, { requiresLogin: true });
    }

    deleteEvent(id: string): Observable<void> {
        return this.http.delete(`${this.baseUrl}/${id}`, undefined, { requiresLogin: true });
    }

    cancelEvent(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/cancel`, {}, { requiresLogin: true });
    }

    listRegistrations(eventId: string): Observable<EventRegistration[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/registrations`, { requiresLogin: true });
    }

    register(eventId: string, registration: Partial<EventRegistration>): Observable<EventRegistration> {
        return this.http.post(`${this.baseUrl}/${eventId}/registrations`, registration, { requiresLogin: true });
    }

    getMyRegistration(eventId: string): Observable<EventRegistration | null> {
        return this.http.get(`${this.baseUrl}/${eventId}/registrations/mine`, { requiresLogin: true });
    }

    updateRegistration(eventId: string, registrationId: string, data: Partial<EventRegistration>): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}`, data, { requiresLogin: true });
    }

    updateRegistrationStatus(eventId: string, registrationId: string, status: string): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/status`, { status }, { requiresLogin: true });
    }

    submitList(
        eventId: string,
        registrationId: string,
        data: { slotLabel: string; forceId: string; snapshotData: any }
    ): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/submit-list`, data, { requiresLogin: true });
    }

    withdrawList(eventId: string, registrationId: string, slotLabel: string): Observable<EventRegistration> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/registrations/${registrationId}/withdraw-list`,
            { slotLabel },
            { requiresLogin: true }
        );
    }

    getRounds(eventId: string): Observable<EventRound[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/rounds`, { requiresLogin: true });
    }

    startRound(eventId: string, roundNumber: number): Observable<EventRound> {
        return this.http.post(`${this.baseUrl}/${eventId}/rounds`, { roundNumber }, { requiresLogin: true });
    }

    updateRound(eventId: string, roundId: string, data: Partial<EventRound>): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}`, data, { requiresLogin: true });
    }

    getStandings(eventId: string): Observable<EventStanding[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/standings`, { requiresLogin: false });
    }

    getForceEventSubmission(forceId: string): Observable<ForceEventSubmission | null> {
        return this.http.get(`${this.baseUrl}/force-submission/${forceId}`, { requiresLogin: true });
    }
}
